import { useEffect, useState } from 'react'
import { api, imHintergrund, periodenname } from './api'
import type { Seriestand } from './Spiel'
import { t } from './sprache'

/**
 * Serie und Bestzeit über der Startseite (7.7, R923).
 *
 * Eine ruhige Zeile über der Arbeitsliste bzw. der Abschlusstafel, an
 * derselben Stelle wie der Erfolgsmoment. Beides gehört dem Kreis in der
 * gewählten Bewertungsebene — ein Mannschaftswert, keine Aussage über einen
 * Menschen.
 *
 * **Die Anwendung rechnet, die Oberfläche setzt nur zusammen.** Wer den
 * Baustein aus hat, bekommt `zeigen: false` und sieht nichts; dann zeigt die
 * Startseite genau, was sie ohne diese Schicht zeigt (R920).
 *
 * Gefragt wird neu, wenn sich der Abschlussstatus ändert — nach einer
 * Freigabe steht die neue Serie gleich da.
 */
export function Seriezeile({
  kreis,
  ledger,
  abschlussstatus,
}: {
  kreis: string
  ledger: string
  abschlussstatus: string | null
}) {
  const [stand, setStand] = useState<Seriestand | null>(null)

  useEffect(() => {
    setStand(null)

    if (!kreis || !ledger) {
      return
    }

    let gilt = true

    // Fehlt die Zeile, fehlt nichts, was jemand erledigen müsste — also im
    // Hintergrund (7.2 Abschnitt 30, R1007).
    imHintergrund(() => api.serie(kreis, ledger))
      .then((s) => gilt && setStand(s))
      .catch(() => undefined)

    return () => {
      gilt = false
    }
  }, [kreis, ledger, abschlussstatus])

  if (!stand?.zeigen || (stand.serie === 0 && !stand.bestzeit)) {
    return null
  }

  const teile = [
    stand.serie === 0
      ? t('spiel.serie.keine')
      : t(stand.serie === 1 ? 'spiel.serie.eins' : 'spiel.serie.mehrere', { anzahl: String(stand.serie) }),
  ]

  if (stand.bestzeit) {
    teile.push(
      t(stand.bestzeit.tage === 1 ? 'spiel.bestzeit.eins' : 'spiel.bestzeit.mehrere', {
        tage: String(stand.bestzeit.tage),
        periode: periodenname(stand.bestzeit.periode),
      }),
    )
  }

  if (stand.zuletzt) {
    teile.push(
      t(stand.zuletzt.tage === 1 ? 'spiel.zuletzt.eins' : 'spiel.zuletzt.mehrere', {
        tage: String(stand.zuletzt.tage),
        periode: periodenname(stand.zuletzt.periode),
      }),
    )
  }

  return <p className="seriezeile">{teile.join(' · ')}</p>
}
